import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import api from '../utils/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { formatStandard } from '../utils/standardLabel';
import './Orders.css';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancellingOrderId, setCancellingOrderId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const paymentCancelled = searchParams.get('cancelled') === 'true';

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    // Clear the cancelled parameter from URL after showing message
    if (paymentCancelled) {
      const timer = setTimeout(() => {
        setSearchParams({}, { replace: true });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [paymentCancelled, setSearchParams]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await api.get('/orders');
      setOrders(response.data.orders);
      setError('');
    } catch (err) {
      setError('Failed to load orders. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return '⏳';
      case 'confirmed':
        return '✅';
      case 'processing':
        return '🔄';
      case 'shipped':
        return '📦';
      case 'delivered':
        return '🎉';
      case 'cancelled':
        return '❌';
      default:
        return '📋';
    }
  };

  const getPaymentStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return '💳';
      case 'pending':
        return '⏱️';
      case 'failed':
        return '⚠️';
      case 'refunded':
        return '↩️';
      default:
        return '💰';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getOrderStages = (status) => {
    const stages = [
      { key: 'pending', label: 'Pending', icon: '⏳' },
      { key: 'confirmed', label: 'Confirmed', icon: '✅' },
      { key: 'processing', label: 'Processing', icon: '🔄' },
      { key: 'shipped', label: 'Shipped', icon: '📦' },
      { key: 'delivered', label: 'Delivered', icon: '🎉' }
    ];

    const statusLower = status.toLowerCase();
    const currentIndex = stages.findIndex(s => s.key === statusLower);
    
    if (statusLower === 'cancelled') {
      return { stages: [], isCancelled: true };
    }

    return {
      stages: stages.map((stage, index) => ({
        ...stage,
        completed: index < currentIndex,
        active: index === currentIndex,
        pending: index > currentIndex
      })),
      isCancelled: false
    };
  };

  const handleCancelOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to cancel this order? This action cannot be undone.')) {
      return;
    }

    try {
      setCancellingOrderId(orderId);
      setError('');
      await api.delete(`/orders/${orderId}`);
      
      // Refresh orders list
      await fetchOrders();
      
      // Dispatch cart update event in case items need to be restored
      window.dispatchEvent(new Event('cartUpdated'));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel order. Please try again.');
      console.error(err);
    } finally {
      setCancellingOrderId(null);
    }
  };

  const handlePayNow = async (orderId) => {
    try {
      setError('');
      const response = await api.post(`/orders/${orderId}/payment-url`);
      
      if (response.data.payment_url) {
        // Redirect to payment page
        window.location.href = response.data.payment_url;
      } else {
        setError('Failed to generate payment URL. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to initiate payment. Please try again.');
      console.error(err);
    }
  };

  const exportToExcel = () => {
    if (orders.length === 0) {
      alert('No orders to export');
      return;
    }

    const data = orders.map(order => {
      const items = Array.isArray(order.items) ? order.items : [];
      const totalAmount = parseFloat(order.total_amount) + parseFloat(order.delivery_charge);
      
      // Create a row for each order with item details
      if (items.length > 0) {
        return items.map((item, idx) => ({
          'Order Number': idx === 0 ? order.order_number : '',
          'Order Date': idx === 0 ? formatDate(order.created_at) : '',
          'Order Status': idx === 0 ? order.status : '',
          'Payment Status': idx === 0 ? order.payment_status : '',
          'Kit Name': item.kit_name || '',
          'Standard': formatStandard(item.standard) || '',
          'Quantity': item.quantity || '',
          'Item Price': parseFloat(item.price || 0).toFixed(2),
          'Subtotal': idx === 0 ? parseFloat(order.total_amount).toFixed(2) : '',
          'Delivery Charge': idx === 0 ? parseFloat(order.delivery_charge).toFixed(2) : '',
          'Total Amount': idx === 0 ? totalAmount.toFixed(2) : ''
        }));
      } else {
        return [{
          'Order Number': order.order_number,
          'Order Date': formatDate(order.created_at),
          'Order Status': order.status,
          'Payment Status': order.payment_status,
          'Kit Name': '',
          'Standard': '',
          'Quantity': '',
          'Item Price': '',
          'Subtotal': parseFloat(order.total_amount).toFixed(2),
          'Delivery Charge': parseFloat(order.delivery_charge).toFixed(2),
          'Total Amount': totalAmount.toFixed(2)
        }];
      }
    }).flat();

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'My Orders');
    XLSX.writeFile(wb, `My-Orders-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  if (loading) {
    return (
      <div className="App">
        <Header />
        <div className="orders-page">
          <div className="container">
            <div className="orders-loading">
              <div className="loading-spinner"></div>
              <p>Loading your orders...</p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App">
      <Header />
      <div className="orders-page">
        <div className="container">
          <div className="orders-header">
            <div className="orders-header-content">
              <h1 className="orders-page-title">
                <span className="orders-icon">📦</span>
                My Orders
              </h1>
              <p className="orders-subtitle">
                {orders.length === 0 
                  ? 'Your order history is empty' 
                  : `${orders.length} ${orders.length === 1 ? 'order' : 'orders'} found`}
              </p>
            </div>
            {orders.length > 0 && (
              <button onClick={exportToExcel} className="btn-export-excel">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                <span>Export to Excel</span>
              </button>
            )}
          </div>

          {paymentCancelled && (
            <div className="orders-info" style={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '8px',
              padding: '15px 20px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#856404" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <div>
                <strong style={{ color: '#856404', display: 'block', marginBottom: '4px' }}>Payment Cancelled</strong>
                <span style={{ color: '#856404' }}>Your payment was cancelled. Your order is still pending payment. You can complete the payment from your orders below.</span>
              </div>
            </div>
          )}

          {error && (
            <div className="orders-error">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <span>{error}</span>
            </div>
          )}

          {orders.length === 0 ? (
            <div className="orders-empty-state">
              <div className="empty-orders-icon">
                <svg width="120" height="120" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                  <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                  <line x1="12" y1="22.08" x2="12" y2="12"></line>
                </svg>
              </div>
              <h2>No Orders Yet</h2>
              <p>You haven't placed any orders yet. Start shopping to see your orders here!</p>
              <Link to="/schools" className="btn-orders-primary">
                <span>Browse Schools</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14"></path>
                  <path d="M12 5l7 7-7 7"></path>
                </svg>
              </Link>
            </div>
          ) : (
            <div className="orders-list-modern">
              {orders.map((order, index) => {
                const items = Array.isArray(order.items) ? order.items : [];
                return (
                  <div 
                    key={order.id} 
                    className="order-card-modern"
                    style={{ animationDelay: `${index * 0.08}s` }}
                  >
                    <div className="order-card-header">
                      <div className="order-header-left">
                        <div className="order-number-section">
                          <span className="order-number-label">Order Number</span>
                          <h3 className="order-number">#{order.order_number}</h3>
                        </div>
                        <div className="order-date-section">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="16" y1="2" x2="16" y2="6"></line>
                            <line x1="8" y1="2" x2="8" y2="6"></line>
                            <line x1="3" y1="10" x2="21" y2="10"></line>
                          </svg>
                          <span className="order-date">{formatDate(order.created_at)}</span>
                        </div>
                      </div>
                      <div className="order-status-badges">
                        {order.status.toLowerCase() !== 'pending' && (
                          <div className={`status-badge status-${order.status.toLowerCase()}`}>
                            <span className="status-icon">{getStatusIcon(order.status)}</span>
                            <span className="status-text">{order.status}</span>
                          </div>
                        )}
                        {order.status.toLowerCase() !== order.payment_status.toLowerCase() && order.payment_status.toLowerCase() !== 'pending' && (
                          <div className={`status-badge status-payment-${order.payment_status.toLowerCase()}`}>
                            <span className="status-icon">{getPaymentStatusIcon(order.payment_status)}</span>
                            <span className="status-text">{order.payment_status}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Order Status Workflow */}
                    {(() => {
                      const workflow = getOrderStages(order.status);
                      if (workflow.isCancelled) {
                        return (
                          <div className="order-workflow-container">
                            <div className="workflow-cancelled">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="15" y1="9" x2="9" y2="15"></line>
                                <line x1="9" y1="9" x2="15" y2="15"></line>
                              </svg>
                              <span>Order Cancelled</span>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div className="order-workflow-container">
                          <div className="order-workflow">
                            {workflow.stages.map((stage, index) => (
                              <React.Fragment key={stage.key}>
                                <div className={`workflow-step ${stage.completed ? 'completed' : ''} ${stage.active ? 'active' : ''} ${stage.pending ? 'pending' : ''}`}>
                                  <div className="workflow-step-icon">
                                    {stage.completed ? (
                                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                      </svg>
                                    ) : (
                                      <span className="workflow-icon-text">{stage.icon}</span>
                                    )}
                                  </div>
                                  <div className="workflow-step-label">{stage.label}</div>
                                </div>
                                {index < workflow.stages.length - 1 && (
                                  <div className={`workflow-connector ${stage.completed ? 'completed' : ''} ${stage.active ? 'active' : ''}`}></div>
                                )}
                              </React.Fragment>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    <div className="order-items-section">
                      <div className="order-items-header">
                        <span className="items-label">Items ({items.length})</span>
                      </div>
                      <div className="order-items-list">
                        {items.map((item, idx) => (
                          <div key={idx} className="order-item-modern">
                            <div className="order-item-info">
                              <span className="item-name">{item.kit_name}</span>
                              <span className="item-standard">{formatStandard(item.standard)}</span>
                            </div>
                            <div className="order-item-details">
                              <span className="item-quantity">Qty: {item.quantity}</span>
                              <span className="item-price">₹{(parseFloat(item.price) * item.quantity).toFixed(2)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="order-card-footer">
                      <div className="order-total-section">
                        <div className="order-total-breakdown">
                          <div className="total-row">
                            <span className="total-label">Subtotal</span>
                            <span className="total-value">₹{parseFloat(order.total_amount).toFixed(2)}</span>
                          </div>
                          {parseFloat(order.delivery_charge) > 0 && (
                            <div className="total-row">
                              <span className="total-label">Delivery</span>
                              <span className="total-value">₹{parseFloat(order.delivery_charge).toFixed(2)}</span>
                            </div>
                          )}
                        </div>
                        <div className="order-grand-total">
                          <span className="grand-total-label">Total Amount</span>
                          <span className="grand-total-amount">
                            ₹{(parseFloat(order.total_amount) + parseFloat(order.delivery_charge)).toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <div className="order-actions">
                        {((order.status.toLowerCase() === 'pending' && order.payment_status.toLowerCase() === 'pending') ||
                          (order.status.toLowerCase() === 'pending' && order.payment_status.toLowerCase() === 'failed')) && (
                          <>
                            <button
                              onClick={() => handlePayNow(order.id)}
                              className="pay-now-btn"
                              style={{
                                backgroundColor: order.payment_status.toLowerCase() === 'failed' ? '#FF9800' : '#4CAF50',
                                color: 'white',
                                border: 'none',
                                padding: '10px 20px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                fontWeight: '600',
                                fontSize: '14px'
                              }}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                              </svg>
                              <span>{order.payment_status.toLowerCase() === 'failed' ? 'Retry Payment' : 'Pay Now'}</span>
                            </button>
                            {order.payment_status.toLowerCase() === 'pending' && (
                              <button
                                onClick={() => handleCancelOrder(order.id)}
                                className="cancel-order-btn"
                                disabled={cancellingOrderId === order.id}
                              >
                                {cancellingOrderId === order.id ? (
                                  <>
                                    <svg className="spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <circle cx="12" cy="12" r="10" strokeOpacity="0.25"></circle>
                                      <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"></path>
                                    </svg>
                                    <span>Cancelling...</span>
                                  </>
                                ) : (
                                  <>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <circle cx="12" cy="12" r="10"></circle>
                                      <line x1="15" y1="9" x2="9" y2="15"></line>
                                      <line x1="9" y1="9" x2="15" y2="15"></line>
                                    </svg>
                                    <span>Cancel Order</span>
                                  </>
                                )}
                              </button>
                            )}
                          </>
                        )}
                        {order.status.toLowerCase() === 'pending' && order.payment_status.toLowerCase() !== 'pending' && (
                          <button
                            onClick={() => handleCancelOrder(order.id)}
                            className="cancel-order-btn"
                            disabled={cancellingOrderId === order.id}
                          >
                            {cancellingOrderId === order.id ? (
                              <>
                                <svg className="spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <circle cx="12" cy="12" r="10" strokeOpacity="0.25"></circle>
                                  <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"></path>
                                </svg>
                                <span>Cancelling...</span>
                              </>
                            ) : (
                              <>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <circle cx="12" cy="12" r="10"></circle>
                                  <line x1="15" y1="9" x2="9" y2="15"></line>
                                  <line x1="9" y1="9" x2="15" y2="15"></line>
                                </svg>
                                <span>Cancel Order</span>
                              </>
                            )}
                          </button>
                        )}
                        <Link
                          to={`/order-confirmation/${order.id}`}
                          className="view-details-btn"
                        >
                          <span>View Details</span>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M5 12h14"></path>
                            <path d="M12 5l7 7-7 7"></path>
                          </svg>
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Orders;
